import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PageTransition, FadeIn, SlideIn, StaggerContainer, StaggerItem } from '../components/layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ChevronRight, Loader2, Tag, FolderTree } from 'lucide-react';
import SignatureBlock from '../components/SignatureBlock';
import axios from 'axios';
import prestOnglerie from '../assets/images/home-bloc1.png';
import logoDsrs from '../assets/logos/logodsrs.png';
import { toAbsoluteUrl } from '../lib/utils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OngleriePedicurePage() {
  const [services, setServices] = useState({ onglerie: [], pedicure: [] });
  const [subcategories, setSubcategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('onglerie');
  const location = useLocation();
  const handledHashRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [onglerieRes, pedicureRes, subcatRes] = await Promise.all([
          axios.get(`${API_URL}/services/category/onglerie`),
          axios.get(`${API_URL}/services/category/pedicure`),
          axios.get(`${API_URL}/services/subcategories`)
        ]);
        setServices({
          onglerie: onglerieRes.data,
          pedicure: pedicureRes.data
        });
        setSubcategories(subcatRes.data);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (isLoading || !location.hash) {
      return;
    }

    // Handle a given hash only once, otherwise it keeps re-forcing the tab
    // and makes the Tabs feel "locked" after navigation.
    if (handledHashRef.current === location.hash) {
      return;
    }

    const targetId = location.hash.replace('#', '');
    if (!targetId) {
      return;
    }

    const isOnglerieTarget = services.onglerie.some(
      (service) => `service-${service.id}` === targetId
    );
    const isPedicureTarget = services.pedicure.some(
      (service) => `service-${service.id}` === targetId
    );

    const desiredTab = isPedicureTarget
      ? 'pedicure'
      : isOnglerieTarget
      ? 'onglerie'
      : null;

    if (desiredTab && desiredTab !== activeTab) {
      setActiveTab(desiredTab);
    }

    const t = setTimeout(() => {
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Mark hash as handled and remove it so tab switching isn't affected later.
      handledHashRef.current = location.hash;
      window.history.replaceState(null, '', location.pathname + location.search);
    }, 120);

    return () => clearTimeout(t);
  }, [isLoading, location.hash, services, activeTab, location.pathname, location.search]);

  // Group services by subcategory
  const groupServicesBySubcategory = (serviceList, categoryName) => {
    const categorySubcats = subcategories.filter(s => s.parent_category === categoryName);
    
    // Services without subcategory
    const uncategorized = serviceList.filter(s => !s.subcategory_id);
    
    // Services grouped by subcategory
    const grouped = categorySubcats
      .filter(subcat => serviceList.some(s => s.subcategory_id === subcat.id))
      .map(subcat => ({
        subcategory: subcat,
        services: serviceList.filter(s => s.subcategory_id === subcat.id)
      }));
    
    return { grouped, uncategorized };
  };

  return (
    <PageTransition>
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden" data-testid="onglerie-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${prestOnglerie})`,
            filter: 'brightness(0.5)'
          }}
        />
        <div className="relative z-10 text-center text-white px-6">
          <p className="text-xs uppercase tracking-[0.4em] text-[var(--rose-gold)] mb-4">
            Nos Prestations
          </p>
          <h1 className="font-['Cormorant_Garamond'] text-5xl md:text-7xl font-light mb-4">
            Onglerie & Pédicure
          </h1>
          <p className="text-lg text-white/80 max-w-xl mx-auto">
            Des soins experts pour sublimer vos mains et vos pieds
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)] transition-colors">Accueil</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Onglerie & Pédicure</span>
          </nav>
        </div>
      </div>

      {/* Signature Block - Featured Services */}
      <SignatureBlock
        categoryType="onglerie_pedicure"
        title="Nos Prestations Signature"
        subtitle="Découvrez nos soins phares sélectionnés pour vous offrir le meilleur de l'onglerie et de la pédicure."
        ctaLink="/onglerie-pedicure"
        ctaLabel="Réserver ce soin"
      />

      {/* Gift Card Banner */}
      <section className="py-12 px-6 bg-gradient-to-r from-[var(--champagne)]/30 to-white">
        <div className="max-w-[800px] mx-auto text-center">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--dark-rosegold)] mb-3">
              Idée Cadeau
            </p>
            <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-light text-[var(--charcoal)] mb-4">
              Offrez un moment de beauté
            </h3>
            <p className="text-sm text-[var(--charcoal)]/70 mb-6 max-w-md mx-auto">
              Faites plaisir à vos proches avec une carte cadeau valable sur toutes nos prestations
            </p>
            <Link
              to="/carte-cadeau"
              className="inline-block px-8 py-3 bg-[var(--gold)] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--dark-gold)] transition-colors duration-300"
              data-testid="giftcard-banner-btn"
            >
              Offrir une carte cadeau
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Services Content */}
      <section className="py-24 px-6" data-testid="services-content">
        <div className="max-w-[1200px] mx-auto">
          <FadeIn className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--dark-rosegold)] mb-4">
              Tarifs & Prestations
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[var(--charcoal)]">
              Choisissez votre soin
            </h2>
          </FadeIn>

          {isLoading ? (
            <div className="text-center py-12">
              <Loader2 size={32} className="animate-spin mx-auto mb-4 text-[var(--gold)]" />
              <p className="text-sm text-[var(--charcoal)]/60">Chargement des prestations...</p>
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full flex justify-center mb-12 bg-transparent gap-4" data-testid="service-tabs">
                <TabsTrigger
                  value="onglerie"
                  data-testid="tab-onglerie"
                  className="px-8 py-3 text-xs uppercase tracking-[0.2em] font-medium border border-[var(--champagne)] data-[state=active]:bg-[var(--gold)] data-[state=active]:text-white data-[state=active]:border-[var(--gold)] transition-all"
                >
                  Onglerie ({services.onglerie.length})
                </TabsTrigger>
                <TabsTrigger
                  value="pedicure"
                  data-testid="tab-pedicure"
                  className="px-8 py-3 text-xs uppercase tracking-[0.2em] font-medium border border-[var(--champagne)] data-[state=active]:bg-[var(--gold)] data-[state=active]:text-white data-[state=active]:border-[var(--gold)] transition-all"
                >
                  Pédicure ({services.pedicure.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="onglerie" data-testid="content-onglerie">
                {services.onglerie.length === 0 ? (
                  <div className="text-center py-12 bg-white border border-[var(--champagne)]">
                    <p className="text-[var(--charcoal)]/60">Aucune prestation disponible pour le moment.</p>
                  </div>
                ) : (
                  <ServiceListWithSubcategories 
                    services={services.onglerie} 
                    categoryName="onglerie"
                    subcategories={subcategories}
                  />
                )}
              </TabsContent>

              <TabsContent value="pedicure" data-testid="content-pedicure">
                {services.pedicure.length === 0 ? (
                  <div className="text-center py-12 bg-white border border-[var(--champagne)]">
                    <p className="text-[var(--charcoal)]/60">Aucune prestation disponible pour le moment.</p>
                  </div>
                ) : (
                  <ServiceListWithSubcategories 
                    services={services.pedicure} 
                    categoryName="pedicure"
                    subcategories={subcategories}
                  />
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[var(--charcoal)]" data-testid="cta-section">
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <FadeIn>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl text-white mb-6">
              Réservez votre soin
            </h2>
            <p className="text-white/70 mb-8">
              Prenez rendez-vous en ligne et offrez-vous un moment de beauté.
            </p>
            <a
              href={process.env.REACT_APP_PLANITY_URL || 'https://www.planity.com/madame-dsrs-95570-attainville'}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="onglerie-cta-planity"
              className="inline-block px-10 py-4 bg-[var(--gold)] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--dark-gold)] transition-colors duration-300"
            >
              Prendre rendez-vous
            </a>
          </FadeIn>
        </div>
      </section>
    </PageTransition>
  );
}

function ServiceCard({ service, index }) {
  const isPromo = service.is_promo && service.promo_price;
  const imageUrl = service.no_photo ? null : toAbsoluteUrl(service.image_url || service.image || service.photo);
  const fallbackSrc = imageUrl || logoDsrs;
  const priceLabel = isPromo ? service.promo_price?.toFixed(2) : service.price?.toFixed(2);
  const durationLabel = service.duration_minutes ? `${service.duration_minutes} MIN` : null;
  const [isExpanded, setIsExpanded] = useState(false);
  const descriptionStyle = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: isExpanded ? 'unset' : 3,
    overflow: isExpanded ? 'visible' : 'hidden',
    textOverflow: 'ellipsis'
  };
  
  return (
    <div
      id={`service-${service.id}`}
      data-testid={`service-item-${index}`}
      className={`group bg-white border transition-all duration-300 flex flex-col md:flex-row overflow-hidden items-stretch md:min-h-[320px] ${
        isExpanded ? 'md:h-auto' : 'md:h-[384px]'
      } ${
        isPromo
          ? 'border-green-200 bg-green-50/30 hover:border-green-300'
          : 'border-[var(--champagne)] hover:border-[var(--gold)]/30'
      } shadow-[0_10px_30px_rgba(0,0,0,0.05)] hover:-translate-y-1`}
    >
      <div className={`md:w-[40%] aspect-[5/4] md:aspect-auto bg-[var(--champagne)]/5 overflow-hidden relative ${
        isExpanded ? 'md:min-h-[384px]' : 'md:h-full'
      }`}>
        {isExpanded && (
          <img
            src={fallbackSrc}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-40"
          />
        )}
        <img
          src={fallbackSrc}
          alt={service.name}
          className={
            isExpanded
              ? "relative w-full h-full object-contain p-4"
              : "relative w-full h-full object-cover"
          }
          onError={(e) => {
            e.currentTarget.src = logoDsrs;
          }}
        />
      </div>
      <div className="flex-1 px-6 py-6 md:px-10 md:py-8 flex flex-col">
        <div className="border-b border-[var(--champagne)]/40 pb-4 mb-5">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-[26px] text-[var(--charcoal)] uppercase tracking-[2px]">
              {service.name}
            </h3>
            {isPromo && (
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs uppercase tracking-wider flex items-center gap-1">
                <Tag size={12} />
                Promo
              </span>
            )}
          </div>
          <div className="text-[13px] text-[var(--gold)] uppercase tracking-[1.5px] mt-2">
            {[durationLabel, priceLabel && `${priceLabel} €`].filter(Boolean).join(' • ')}
          </div>
        </div>
        <p
          className="text-sm leading-[1.8] text-[#666] mb-4 font-light"
          style={descriptionStyle}
        >
          {service.description}
        </p>
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          className="text-[11px] uppercase tracking-[2px] text-[var(--gold)] border-none bg-transparent p-0 mb-6"
        >
          {isExpanded ? '- VOIR MOINS' : '+ VOIR PLUS'}
        </button>
        <div className="flex items-center justify-between">
          <a
            href={process.env.REACT_APP_PLANITY_URL || 'https://www.planity.com/madame-dsrs-95570-attainville'}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-[var(--gold)] text-[var(--gold)] px-6 py-3 text-[11px] uppercase tracking-[2px] font-medium transition-all duration-300 hover:bg-[var(--gold)] hover:text-white"
          >
            Réserver ce soin
          </a>
          {isPromo && service.price && (
            <span className="text-[var(--charcoal)]/40 line-through text-xs">
              {service.price.toFixed(2)}€
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ServiceListWithSubcategories({ services, categoryName, subcategories }) {
  // Get subcategories for this category
  const categorySubcats = subcategories.filter(s => s.parent_category === categoryName);
  
  // Services without subcategory
  const uncategorized = services.filter(s => !s.subcategory_id);
  
  // Services grouped by subcategory
  const grouped = categorySubcats
    .filter(subcat => services.some(s => s.subcategory_id === subcat.id))
    .map(subcat => ({
      subcategory: subcat,
      services: services.filter(s => s.subcategory_id === subcat.id)
    }));

  // If no subcategories with services, just show all services
  if (grouped.length === 0) {
    return (
      <StaggerContainer className="space-y-4">
        {services.map((service, index) => (
          <StaggerItem key={service.id}>
            <ServiceCard service={service} index={index} />
          </StaggerItem>
        ))}
      </StaggerContainer>
    );
  }

  return (
    <div className="space-y-10">
      {/* Grouped by subcategory */}
      {grouped.map(group => (
        <div key={group.subcategory.id}>
          <div className="flex items-center gap-3 mb-4 pb-2 border-b border-[var(--champagne)]">
            <FolderTree size={18} className="text-[var(--gold)]" />
            <h3 className="font-['Cormorant_Garamond'] text-2xl text-[var(--charcoal)]">
              {group.subcategory.name}
            </h3>
            <span className="text-sm text-[var(--charcoal)]/40">
              ({group.services.length} prestation{group.services.length > 1 ? 's' : ''})
            </span>
          </div>
          <StaggerContainer className="space-y-4">
            {group.services.map((service, index) => (
              <StaggerItem key={service.id}>
                <ServiceCard service={service} index={index} />
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      ))}

      {/* Uncategorized services */}
      {uncategorized.length > 0 && (
        <div>
          {grouped.length > 0 && (
            <div className="flex items-center gap-3 mb-4 pb-2 border-b border-[var(--champagne)]">
              <h3 className="font-['Cormorant_Garamond'] text-2xl text-[var(--charcoal)]">
                Autres prestations
              </h3>
              <span className="text-sm text-[var(--charcoal)]/40">
                ({uncategorized.length})
              </span>
            </div>
          )}
          <StaggerContainer className="space-y-4">
            {uncategorized.map((service, index) => (
              <StaggerItem key={service.id}>
                <ServiceCard service={service} index={index} />
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      )}
    </div>
  );
}
